import type { Types } from '@ohif/core';
import React from 'react';
import {
  VIEWPORT_OVERLAY_BOTTOM_LEFT,
  VIEWPORT_OVERLAY_TOP_LEFT,
} from './components/overlay/viewportOverlayItems';
import DicomUpload from './components/DicomUpload';
import DeleteAction from './components/WorklistDelete';
import SycaiLoadingIndicator from './components/SycaiLoadingIndicator';
import SycaiLoadingIndicatorTotalPercent from './components/SycaiLoadingIndicatorTotalPercent';
import { isDownloadableDisplaySet } from './utils/isDownloadableDisplaySet';

/**
 * The same two-key discipline M1 proved. Pushing the id alone renders nothing and warns
 * nothing — getButtonSection maps the unknown id to undefined and useToolbar strips it
 * with .filter(Boolean) — and updateSection:493-504 validates nothing either.
 *
 * `props` must exist or _mapButtonToDisplay:576 destructures undefined.
 *
 * The id is BARE, matching 100% of upstream ids, but carries a fork prefix because the
 * merge target is flat: ToolbarService.state.buttons is a plain object keyed by
 * button.id into which modes flatten every pack, and register():199-210 skips a
 * duplicate id silently, first-writer-wins, with no warning. cornerstone.toolbarButtons
 * and tmtv.toolbarButtons already share 21 identical ids (§5.8).
 */
export const SYCAI_DIAGNOSIS_BUTTON = {
  id: 'SycaiDiagnosis',
  uiType: 'sycai.diagnosisButton',
  /**
   * `label` is gone: DiagnosisButton derives its label from the diagnosis state
   * (ButtonContent's CONTENT map), so a definition-supplied one could only ever
   * disagree with what is rendered. `tooltip` stays and is now read.
   */
  props: {
    tooltip: 'Run the SYCAI diagnosis for this study',
  },
};

export const SYCAI_ORGAN_REVIEW_BUTTON = {
  id: 'SycaiOrganReview',
  uiType: 'sycai.organReviewButton',
  props: {
    tooltip: 'Review organ image quality for this series',
  },
};

/**
 * The entry NAME selects the scope. CustomizationService.init auto-merges exactly two
 * slots per extension: `${extensionId}.customizationModule.default` -> Default (:196, :201)
 * and `...global` -> Global (:205, :210). An entry with no `name` throws out of
 * processExtensionModule (ExtensionManager.ts:520-522).
 *
 * Global is the deliberate choice here, not a forced one. Mode.tsx:259-265 seeds
 * toolbarButtons, toolbarSections, leftPanels, rightPanels and toolGroupAdditions as plain
 * arrays at Mode scope, which would zero out a Default-scope $push on THOSE bare keys
 * (_update:1064-1066 returns a value with no `$` key verbatim; getCustomization reads
 * global ?? mode ?? default at :483-486). But this module never touches the bare keys -- it
 * patches `cornerstone.toolbarButtons` / `cornerstone.toolbarSections`, which Mode.tsx does
 * not seed at all; modes/basic/src/index.tsx composes those into the bare keys via
 * `{ $reference: ... }` instead (:329, :370). A Default-scope $push against the
 * `cornerstone.*` keys does resolve correctly through that reference chain -- confirmed in
 * review by running the real chain with this module's entry renamed to `default`. Assertion
 * 3 characterises the bare-key hazard on upstream directly; it does not exercise this
 * module -- see its comment.
 *
 * What Global costs instead, and this is the actual reason it needs care: setGlobalCustomization
 * (:1022) bases its update on `_cloneIfNeeded(global) || default` -- it SNAPSHOTS Default at
 * merge time -- and getCustomization resolves global ?? mode ?? default (:483-486). So once
 * sycai's Global entry is in place, it wins permanently, and any Default-scope
 * `cornerstone.toolbar*` contribution registered by an extension AFTER sycai becomes
 * invisible, silently -- no warning, no error. This is live, not hypothetical: appInit.js:93
 * loads `[...defaultExtensions, ...appConfig.extensions]`, and sycai ships in
 * `defaultExtensions` via pluginConfig.json, so anything supplied through
 * `window.config.extensions` registers after it. Dormant today only because
 * `platform/app/public/config/sycai-local.js:16` is `extensions: []` -- M2's deploy configs
 * will change that. Tracked in `docs/superpowers/specs/m1-skeleton/findings.md`'s "Carried
 * forward" section.
 *
 * `cornerstone.toolbarSections` is an OBJECT (toolbarButtonsCustomization.ts:718), so the
 * $push nests per section. `primary` -- not `MoreTools` -- because MoreTools is an
 * `ohif.toolButtonList` section whose renderer (ToolButtonListWrapper) never reads
 * `Component`; only top-level sections rendered by Toolbar.tsx:53-79 do. See plan D-1.
 *
 * Both $push commands need an existing base: setGlobalCustomization:1022 (same mechanic as
 * above) bases the update on _cloneIfNeeded(global) || default, and immutability-helper
 * invariants on undefined. Safe only because sycai is APPENDED after cornerstone in
 * pluginConfig.json. Assertion 4.
 */
export default function getCustomizationModule(
  { servicesManager }: Types.Extensions.ExtensionParams = {} as Types.Extensions.ExtensionParams
) {
  return [
    {
      name: 'global',
      value: {
        'sycai.marker': 'm1',
        'cornerstone.toolbarButtons': {
          $push: [SYCAI_DIAGNOSIS_BUTTON, SYCAI_ORGAN_REVIEW_BUTTON],
        },
        'cornerstone.toolbarSections': {
          primary: { $push: ['SycaiDiagnosis', 'SycaiOrganReview'] },
        },
      },
    },
    {
      /**
       * Plan D-1. NOT the Global entry above, and this is the one scope decision in this file
       * that is not free. getCustomization resolves `global ?? mode ?? default`
       * (CustomizationService.ts:483-486), and modes/usAnnotation/src/index.ts:234-263 sets
       * its own `viewportOverlay.topLeft` -- a BLinePleuraPercentage item -- through
       * setCustomizations(...), whose scope parameter DEFAULTS to Mode (:603-605). A Global
       * entry here would erase that mode's item silently, and
       * @ohif/mode-ultrasound-pleura-bline is registered in pluginConfig.json with no
       * `default: false`, so it is live rather than hypothetical. §10 risk 4's "neither
       * introduces nor worsens it" is corrected by this entry.
       *
       * Default still beats cornerstone's own array: init() merges each extension's `default`
       * slot in getRegisteredExtensionIds() order (:194-203), and sycai is APPENDED after
       * cornerstone in pluginConfig.json -- the same ordering the Global $push above already
       * depends on.
       *
       * `sycai.marker` deliberately stays out of this entry: registration.test.ts asserts it
       * is Global-only, which is what pins the Global slot's scope.
       */
      name: 'default',
      value: {
        'viewportOverlay.topLeft': VIEWPORT_OVERLAY_TOP_LEFT,
        'viewportOverlay.bottomLeft': VIEWPORT_OVERLAY_BOTTOM_LEFT,

        /**
         * D1. Replaces cornerstone's value rather than pushing onto it — this key
         * holds a single component (miscCustomization.ts:20), not an array.
         * Ordering is the same mechanic the two viewportOverlay keys above already
         * depend on: CustomizationService.init merges each extension's `default`
         * slot in getRegisteredExtensionIds() order (:194-203), and sycai is
         * APPENDED after cornerstone in pluginConfig.json.
         *
         * develop instead patched platform/app/src/routes/WorkList/WorkList.tsx
         * (:565-593) and commented the data-source gate out to `&& true`
         * (:570-571). Neither is ported: upstream's own gate is honoured through
         * config, and sycai-local.js:99 already sets dicomUploadEnabled: true.
         */
        dicomUploadComponent: DicomUpload,

        /**
         * Replaces OHIF's LoadingOHIFMark loader with the Sycai mark. Single-component
         * key (extensions/default loadingIndicatorProgressCustomization.tsx), so it is
         * replaced, not $push'd — same mechanic as dicomUploadComponent above: sycai's
         * `default` slot merges after extensions/default's in pluginConfig.json order.
         * Rebrands every loader that reads this key (ViewerLayout, WorkList, Local,
         * DicomMicroscopyViewport).
         */
        'ui.loadingIndicatorProgress': SycaiLoadingIndicator,

        /**
         * The SEG/RT/PMAP viewport loaders read this key, NOT ui.loadingIndicatorProgress,
         * and OHIF's LoadingIndicatorTotalPercent imports LoadingIndicatorProgress directly
         * (bypassing customization) — so the key above never reaches them. Same replace-not-
         * push mechanic and sycai-after-default ordering as above.
         */
        'ui.loadingIndicatorTotalPercent': SycaiLoadingIndicatorTotalPercent,

        /**
         * D2. Edits the existing 'actions' column rather than adding a column:
         * DataTable's FilterRow renders a text input for every visible column
         * and never consults getCanFilter(), so a new column gets a stray
         * filter box that blanks the whole list when typed in, and the only
         * suppression points are hardcoded inside ui-next's Table.tsx.
         * D2b supersedes D2a. The cell is REPLACED, not wrapped: Delete is a
         * dropdown item, so sycai rebuilds the stock cell from the two pieces
         * ui-next now exports for it (StudyList.ActionOverlayCell,
         * StudyList.WorkflowMenu -- both M4.4 upstream exceptions) and adds its
         * item through WorkflowMenu's `extraItems` prop.
         */
        'workList.columns': {
          $apply: (cols: any[]) =>
            cols.map(col => {
              if (col.id !== 'actions') {
                return col;
              }
              return {
                ...col,
                cell: (context: any) => (
                  <DeleteAction
                    cell={context.cell}
                    table={context.table}
                  />
                ),
              };
            }),
        },

        /**
         * D6. Zero-edit addition via upstream's own extensibility point.
         * StudyListSettingsPopover.tsx's defaultSettingsMenuItems already
         * pushes an id: 'logout' item when appConfig.oidc is truthy -- always
         * false in this fork (D7) -- so this uses a DISTINCT id so the two
         * can never collide if that assumption ever changes.
         */
        'workList.settingsMenuItems': (
          defaults: Array<{ id: string; label: React.ReactNode; onClick: () => void }>
        ) => [
          ...defaults,
          {
            id: 'sycaiLogout',
            label: 'Log out',
            onClick: () => servicesManager.services.sessionService.logOut(),
          },
        ],

        'ohif.logoutHandler': () => servicesManager.services.sessionService.logOut(),
        'ohif.currentUsername': () =>
          servicesManager.services.sessionService.getAccount()?.username,

        /**
         * $apply wraps cornerstone's identity default (miscCustomization.ts)
         * rather than replacing it, so any future upstream logic there still
         * runs. Guards getSopClassHandlerModule.ts's
         * _checkIfCanAddMeasurementsToDisplaySet, which indexes
         * measurement.coords[0] unconditionally:
         * _processNonGeometricallyDefinedMeasurement builds `coords: []` for
         * TID 1500 findings recorded with no SCOORD -- sycai's own reports do
         * this when a finding's extent lives in a paired SEG series instead
         * of a drawn region. An empty array throws there, which aborts that
         * whole SR's load and permanently trips SRViewportWrapper's
         * failedDisplaySet fallback, so no measurement from the report -- not
         * just the geometry-less one -- ever becomes hydratable. A coords[0]
         * placeholder with no ReferencedSOPSequence makes the same function's
         * own `if (!referencedSOPSequence) continue` skip it cleanly instead.
         */
        onBeforeSRAddMeasurement: {
          $apply:
            (upstreamOnBeforeSRAddMeasurement: (args: any) => any) =>
            (args: { measurement: any; StudyInstanceUID: string; SeriesInstanceUID: string }) => {
              const { measurement } = args;
              if (!measurement.coords?.length) {
                measurement.coords = [{}];
              }
              return upstreamOnBeforeSRAddMeasurement(args);
            },
        },

        /**
         * D12. $push onto studyBrowser.thumbnailMenuItems -- Default-scope
         * base is extensions/default's own studyBrowserCustomization.ts,
         * registered before sycai in pluginConfig.json.
         */
        'studyBrowser.thumbnailMenuItems': {
          $push: [
            {
              id: 'downloadSeries',
              label: 'Download',
              iconName: 'Download',
              commands: 'downloadSeries',
            },
          ],
        },

        /**
         * D13. $apply, delegating to the captured upstream function for
         * every item except a gated downloadSeries check -- never a bare
         * $set. subItem.selector (the only per-item visibility hook
         * menuContentCustomization.tsx exposes) receives only
         * { servicesManager }, no display set, so it can only gate
         * `disabled`, never hide an item by modality. getCustomizationModule
         * bodies run before CustomizationService.init populates any scope,
         * so a bare replacement would have no "old value" to delegate to at
         * registration time -- $apply's callback receives the live value at
         * MERGE time instead, by which point extensions/default's
         * registration (earlier in pluginConfig.json) has already run.
         */
        'ohif.menuContent': {
          $apply: (upstreamMenuContent: (props: any) => JSX.Element) =>
            function sycaiMenuContent(props: any) {
              const { item, servicesManager, ...rest } = props;

              if (
                item.id === 'downloadSeries' &&
                !isDownloadableDisplaySet(
                  rest.displaySetInstanceUID,
                  servicesManager.services.displaySetService
                )
              ) {
                return null;
              }

              return upstreamMenuContent(props);
            },
        },
      },
    },
  ];
}
